import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, TrendingUp, Euro, ShoppingBag, Trophy } from "lucide-react";
import api from "../../lib/api";

// Onglet "Statistiques" — CA journalier (30j) + plats populaires + totaux
export default function StatsTab() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    (async () => {
      try { const { data: d } = await api.get("/restaurateur/stats"); if (ok) setData(d); }
      catch { /* ignore */ }
      finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, []);

  const totals = data?.totals || {};
  const daily = data?.daily || [];
  const topDishes = data?.top_dishes || [];
  const maxRevenue = useMemo(() => Math.max(1, ...daily.map((d) => d.revenue || 0)), [daily]);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F26419]" /></div>;

  return (
    <div data-testid="rt-stats-tab" className="space-y-6">
      <div>
        <h2 className="font-head font-black text-2xl text-[#1A1A1A]">{t("restaurateur.stats.title")}</h2>
        <p className="text-sm text-[#75807A] mt-1">{t("restaurateur.stats.subtitle")}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard icon={Euro} label={t("restaurateur.stats.kpi.revenueToday")} value={fmtEuro(totals.revenue_today_eur)} sub={`${totals.orders_today || 0} ${t("restaurateur.stats.kpi.ordersLabel")}`} accent />
        <KpiCard icon={TrendingUp} label={t("restaurateur.stats.kpi.revenue7d")} value={fmtEuro(totals.revenue_7d_eur)} sub={`${totals.orders_7d || 0} ${t("restaurateur.stats.kpi.ordersLabel")}`} />
        <KpiCard icon={Euro} label={t("restaurateur.stats.kpi.revenueAll")} value={fmtEuro(totals.revenue_all_eur)} sub={`${totals.orders_paid_all || 0} ${t("restaurateur.stats.kpi.ordersLabel")}`} />
        <KpiCard icon={ShoppingBag} label={t("restaurateur.stats.kpi.ordersTotal")} value={String(totals.orders_total || 0)} sub={t("restaurateur.stats.kpi.allTime")} />
      </div>

      {/* Chart CA 30j */}
      <section className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg text-[#1A1A1A]">{t("restaurateur.stats.chart.title")}</h3>
          <div className="text-xs text-[#75807A] font-semibold">{t("restaurateur.stats.chart.subtitle")}</div>
        </div>
        {daily.every((d) => (d.revenue || 0) === 0) ? (
          <div className="py-8 text-center text-sm text-[#75807A]">{t("restaurateur.stats.chart.empty")}</div>
        ) : (
          <div className="flex items-end gap-1 h-40" data-testid="rt-stats-chart">
            {daily.map((d) => {
              const pct = Math.max(2, Math.round(((d.revenue || 0) / maxRevenue) * 100));
              const label = new Date(d.day).toLocaleDateString(i18n.language === "en" ? "en-GB" : "fr-BE", { day: "2-digit", month: "short" });
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center justify-end group relative" title={`${label}: ${fmtEuro(d.revenue)} · ${d.orders || 0} cmd.`}>
                  <div className="w-full bg-[#F26419]/80 hover:bg-[#F26419] rounded-t transition-colors" style={{ height: `${pct}%` }} />
                  <div className="absolute -top-8 bg-[#1A1A1A] text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                    {label}: {fmtEuro(d.revenue)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-2 flex justify-between text-[10px] text-[#75807A]">
          <span>{new Date(daily[0]?.day || Date.now()).toLocaleDateString(i18n.language === "en" ? "en-GB" : "fr-BE", { day: "2-digit", month: "short" })}</span>
          <span>{t("restaurateur.stats.chart.today")}</span>
        </div>
      </section>

      {/* Top plats */}
      <section className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg text-[#1A1A1A] inline-flex items-center gap-2"><Trophy className="w-5 h-5 text-[#F26419]" /> {t("restaurateur.stats.topDishes.title")}</h3>
          <div className="text-xs text-[#75807A] font-semibold">{t("restaurateur.stats.topDishes.subtitle")}</div>
        </div>
        {topDishes.length === 0 ? (
          <div className="py-6 text-center text-sm text-[#75807A]">{t("restaurateur.stats.topDishes.empty")}</div>
        ) : (
          <ol className="space-y-2">
            {topDishes.map((d, i) => (
              <li key={i} className="flex items-center gap-3 rounded-xl bg-[#FAF5EC] px-3 py-2" data-testid={`rt-stats-top-${i}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center font-bold text-sm shrink-0 ${i === 0 ? "bg-[#F26419] text-white" : "bg-white text-[#1A1A1A]"}`}>{i + 1}</div>
                <div className="flex-1 min-w-0 font-semibold text-[#1A1A1A] truncate">{d.label}</div>
                <div className="text-xs text-[#75807A]"><b className="text-[#1A1A1A]">{d.count}</b> {t("restaurateur.stats.topDishes.ordersLabel")}</div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-gradient-to-br from-[#F26419] to-[#D94D0E] text-white border-[#D94D0E]" : "bg-white border-[#E2DFD8]"}`}>
      <Icon className={`w-5 h-5 ${accent ? "opacity-90" : "text-[#F26419]"}`} />
      <div className={`mt-2 text-[11px] font-semibold uppercase tracking-wider ${accent ? "opacity-90" : "text-[#75807A]"}`}>{label}</div>
      <div className={`mt-1 font-head font-black text-2xl ${accent ? "" : "text-[#1A1A1A]"}`}>{value}</div>
      {sub && <div className={`text-[11px] ${accent ? "opacity-90" : "text-[#75807A]"}`}>{sub}</div>}
    </div>
  );
}

function fmtEuro(v) {
  try { return new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(v || 0); }
  catch { return `${(v || 0).toFixed(2)} €`; }
}
